import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { ArrowRight, ShieldCheck, Scale, Users, MapPin, Bell } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import usePageSEO from "@/hooks/usePageSEO";

const TRUST = [
  { Icon: Scale, title: "Multi-carrier choice", body: "We compare across a curated set of A-rated carriers instead of pushing one." },
  { Icon: ShieldCheck, title: "No captive incentives", body: "Independence means we don't have quotas tied to a single carrier." },
  { Icon: Users, title: "Best-fit, not best-commission", body: "Recommendations map to your situation — not to the carrier paying most that month." },
  { Icon: MapPin, title: "State-appropriate coverage", body: "Product availability and rates vary by state; we align to what's approved where you live." },
];

export default function Carriers() {
  const [data, setData] = useState(null);

  usePageSEO({
    title: "Carrier Network — Lifespan Wealth Partners",
    description:
      "Independent insurance producer working with A.M. Best A-rated carriers across life, annuities, disability, long-term care, health, and Medicare product lines.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Lifespan Wealth Partners",
      description: "Independent insurance producer",
      areaServed: "United States",
    },
  });

  useEffect(() => {
    let cancelled = false;
    apiGet("/carriers").then((d) => { if (!cancelled) setData(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const cats = data?.categories || [];

  return (
    <div data-testid="carriers-page" className="min-h-screen bg-lw-ivory">
      <Nav />
      <main className="pt-28 sm:pt-32 lg:pt-36 pb-20">
        {/* Hero */}
        <section className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <span className="lw-eyebrow">Carrier network</span>
          <h1
            data-testid="carriers-headline"
            className="mt-3 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-lw-navy leading-[1.05]"
          >
            Independent. Multi-carrier. Built for you.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-lw-navy/85 leading-relaxed">
            Independence means we&apos;re not captive to one insurance company. We
            place policies across a curated set of highly-rated carriers so the
            recommendation you receive is tuned to your situation — your state,
            your health class, your age band, and the specific product design
            that fits your plan.
          </p>
          <p className="mt-4 text-base sm:text-lg text-lw-navy/85 leading-relaxed">
            Every carrier below is chosen for financial strength, product
            breadth, and consistent claims handling. A.M. Best ratings shown are
            examples as of recent publication — always verify current ratings
            with the carrier or at ambest.com before purchase.
          </p>
        </section>

        {/* Disclaimer banner */}
        <section className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 mt-10">
          <div
            data-testid="carriers-disclaimer"
            className="rounded-2xl border border-lw-gold/40 bg-lw-ivory-warm p-5 sm:p-6"
          >
            <p className="text-xs uppercase tracking-[0.14em] font-semibold text-lw-navy/70">Important</p>
            <p className="mt-2 text-xs sm:text-sm text-lw-navy/85 leading-relaxed">
              {data?.disclaimer || "Loading disclaimer…"}
            </p>
          </div>
        </section>

        {/* Category sections */}
        <section className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 mt-16 space-y-16">
          {cats.map((cat) => (
            <div key={cat.key} data-testid={`carrier-category-${cat.key}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-serif-display text-2xl sm:text-3xl text-lw-navy leading-tight">
                  {cat.category_display}
                </h2>
                {cat.coming_soon && (
                  <span
                    data-testid={`carrier-coming-soon-${cat.key}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-lw-navy text-lw-ivory text-[10px] font-semibold uppercase tracking-[0.14em]"
                  >
                    <Bell size={11} /> Now Onboarding
                  </span>
                )}
              </div>
              <div className="gold-hairline w-16 my-3 opacity-70" />

              {cat.coming_soon_notice && (
                <p className="text-sm text-lw-muted italic mb-4" data-testid={`carrier-notice-${cat.key}`}>
                  {cat.coming_soon_notice}
                </p>
              )}

              {cat.products_covered?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {cat.products_covered.map((slug) => (
                    <Link
                      key={slug}
                      to={`/products/${slug}`}
                      data-testid={`carrier-product-chip-${slug}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-lw-navy/15 text-xs text-lw-navy hover:border-lw-gold transition-colors"
                    >
                      {slug.replace(/-/g, " ")} <ArrowRight size={11} />
                    </Link>
                  ))}
                </div>
              )}

              <div className="overflow-x-auto rounded-2xl border border-lw-navy/10 bg-white">
                <table className="w-full text-sm" data-testid={`carrier-table-${cat.key}`}>
                  <thead>
                    <tr className="bg-lw-navy text-lw-ivory text-left">
                      <th className="p-4 font-sans-ui text-[11px] uppercase tracking-[0.14em]">Carrier</th>
                      <th className="p-4 font-sans-ui text-[11px] uppercase tracking-[0.14em]">A.M. Best</th>
                      <th className="p-4 font-sans-ui text-[11px] uppercase tracking-[0.14em]">Notable for</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cat.carriers.map((c, i) => (
                      <tr key={i} className="border-t border-lw-navy/10 bg-lw-ivory-warm/40">
                        <td className="p-4 font-semibold text-lw-navy">{c.name}</td>
                        <td className="p-4 text-lw-navy/85">{c.am_best_rating}</td>
                        <td className="p-4 text-lw-navy/85">{c.notable_for}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </section>

        {/* Trust cards */}
        <section className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 mt-20">
          <span className="lw-eyebrow">Why independence matters</span>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TRUST.map((t, i) => (
              <div key={i} className="lw-card p-6" data-testid={`trust-card-${i}`}>
                <t.Icon size={20} className="text-lw-gold" />
                <h3 className="mt-3 font-serif-display text-lg text-lw-navy leading-snug">{t.title}</h3>
                <p className="mt-2 text-sm text-lw-muted leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Compliance micro-copy */}
        <section className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 mt-14">
          <p className="text-xs text-lw-muted leading-relaxed">
            Lifespan Wealth Partners is a licensed insurance producer, not a registered investment adviser. Nothing on this page constitutes investment, tax, or legal advice. See our{" "}
            <Link to="/compliance" className="text-lw-navy underline hover:text-lw-gold">Compliance &amp; Disclosures</Link>{" "}
            page for full regulatory information.
          </p>
        </section>

        {/* Final CTA */}
        <section className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 mt-16">
          <div
            className="rounded-3xl p-8 sm:p-12"
            style={{ background: "linear-gradient(135deg, #0B2545 0%, #1E3A8A 100%)" }}
          >
            <div className="max-w-2xl">
              <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-lw-gold">No pressure, no cost</span>
              <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-ivory leading-tight">
                Talk to us about which carrier best fits your situation.
              </h2>
              <Link
                to="/apply?source=carriers"
                data-testid="carriers-final-cta"
                className="btn-gold mt-6 inline-flex"
              >
                Book a conversation <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
